import { useEffect, useMemo, useState } from 'react';
import {
  Bot,
  CheckCircle2,
  FilePenLine,
  FileText,
  FileUp,
  Loader2,
  MessageSquareText,
  SendHorizontal,
  Sparkles,
  Trash2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { showToast } from '@/utils/toast';
import { usePriceIntelStore } from '../stores/price-intel-store';
import { ManualFactForm } from './ManualFactForm';
import {
  askDocumentQA,
  commitParsedPreview,
  parseSourceExtract,
  parseSourceIntent,
  type DocIntent,
  type DocQAResponse,
  type ParseIntentResult,
  type ParsePreviewCandidate,
  type ParsePreviewInput,
  type ParsePreviewResult,
} from '../services/price-intel-service';


type IntakeTab = 'file' | 'text' | 'manual';
type ParseStage = 'idle' | 'parsing' | 'confirming' | 'previewing';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-background-primary)',
  borderColor: 'var(--color-system-quaternary)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-sm)',
};

const mutedText: React.CSSProperties = { color: 'var(--color-system-secondary)' };

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-key)',
  color: '#ffffff',
};

const tabDefs: { id: IntakeTab; label: string; caption: string; icon: LucideIcon }[] = [
  { id: 'file', label: 'AI 解析文件', caption: '上传 PDF / Excel / 图片，AI 自动抽取为结构化字段', icon: FileUp },
  { id: 'text', label: 'AI 解析文本', caption: '粘贴销售线索或报价段落，AI 自动抽取', icon: Sparkles },
  { id: 'manual', label: '手工录入', caption: '员工自己填所有字段，跳过 AI', icon: FilePenLine },
];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function PriceIntelCommandCenter() {
  const navigate = useNavigate();
  const { mode, candidates, refreshAll } = usePriceIntelStore();

  const [activeTab, setActiveTab] = useState<IntakeTab>('file');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sourceTitle, setSourceTitle] = useState('');
  const [inputText, setInputText] = useState('');

  const [stage, setStage] = useState<ParseStage>('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [preview, setPreview] = useState<ParsePreviewResult | null>(null);
  const [intentPreview, setIntentPreview] = useState<ParseIntentResult | null>(null);
  const [pendingInput, setPendingInput] = useState<ParsePreviewInput | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);


  const selectedLabel = useMemo(() => {
    if (selectedFiles.length === 0) return '拖入或选择 PDF / Excel / Word / PPT / CSV / 图片';
    return `${selectedFiles.length} 份资料已加入：${selectedFiles.map((file) => file.name).join(' / ')}`;
  }, [selectedFiles]);

  const recentActivity = useMemo(() => {
    return candidates.slice(0, 4).map((candidate) => ({
      id: candidate.id,
      title: candidate.competitor || candidate.model || '待审核候选',
      meta: candidate.source_record?.source_name || 'pending review',
      href: '/price_intel/reviews',
    }));
  }, [candidates]);

  // 第一段：解析 + 文档指纹 + 意图判定（不调 LLM 抽取），进入 confirming
  const runParseFlow = async (input: ParsePreviewInput) => {
    setStage('parsing');
    setStageError(null);
    setPreview(null);
    setIntentPreview(null);
    setPendingInput(input);
    setCurrentStep(0);

    try {
      const steps = input.kind === 'file'
        ? ['上传文件到后端', '本地结构化解析与表格分块', '识别文档类型与抽取方向']
        : ['规整粘贴文本', '表格/段落分块', '识别文档类型与抽取方向'];
      const fetchPromise = parseSourceIntent(input);

      for (let i = 0; i < steps.length; i += 1) {
        setCurrentStep(i);
        await delay(300 + i * 100);
      }

      const result = await fetchPromise;
      setCurrentStep(steps.length);
      setIntentPreview(result);
      setStage('confirming');
    } catch (error) {
      setStageError((error as Error).message);
      setStage('idle');
    }
  };

  // 第二段：用户确认/纠正意图后，按 intent 定向抽取，进入 previewing
  const runExtractFlow = async (intent: DocIntent, intentSource: 'rule' | 'llm' | 'user_override') => {
    if (!pendingInput) return;
    setExtracting(true);
    setStageError(null);
    try {
      const result = await parseSourceExtract(pendingInput, intent, intentSource);
      setPreview(result);
      setStage('previewing');
    } catch (error) {
      setStageError((error as Error).message);
    } finally {
      setExtracting(false);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showToast('请选择至少一个源文件');
      return;
    }
    await runParseFlow({
      kind: 'file',
      file: selectedFiles[0],
      sourceName: selectedFiles[0].name,
      metadata: { intake: 'dashboard_upload' },
    });
  };

  const handleSubmitText = async () => {
    if (!inputText.trim()) {
      showToast('请输入需要解析的文本源');
      return;
    }
    await runParseFlow({
      kind: 'text',
      sourceName: sourceTitle.trim() || 'Pasted intelligence',
      inputText,
      metadata: { intake: 'dashboard_text' },
    });
  };

  const handleDiscardPreview = () => {
    setPreview(null);
    setIntentPreview(null);
    setPendingInput(null);
    setStage('idle');
    setSelectedFiles([]);
    setInputText('');
    setSourceTitle('');
    showToast('已丢弃解析结果');
  };

  const handleSendToReview = async (edited: ParsePreviewCandidate) => {
    if (!preview) return;
    try {
      await commitParsedPreview(preview, edited);
      await refreshAll();
      setPreview(null);
      setIntentPreview(null);
      setPendingInput(null);
      setSelectedFiles([]);
      setInputText('');
      setSourceTitle('');
      setStage('idle');
      showToast('已推送到审核台');
      navigate('/price_intel/reviews');
    } catch (error) {
      showToast(`提交失败: ${(error as Error).message}`);
    }
  };

  const handleManualSuccess = () => navigate('/price_intel/reviews');

  const currentTab = tabDefs.find((t) => t.id === activeTab)!;

  return (
    <div className="space-y-6">
      <section className="p-6" style={cardStyle}>
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold leading-tight" style={{ color: 'var(--color-system-primary)' }}>
            把竞品情报喂进系统
          </h1>
          <p className="mt-2 text-sm leading-6" style={mutedText}>
            三种录入方式 → 当前页面实时解析 → 预览候选后再推送审核台 → 进入趋势与领导汇报。
          </p>
        </div>

        <div className="mt-6 grid gap-2 sm:grid-cols-3">
          {tabDefs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                disabled={stage !== 'idle'}
                className="flex items-start gap-3 rounded-xl border p-4 text-left transition hover:border-[var(--color-key)] disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  borderColor: isActive ? 'var(--color-key)' : 'var(--color-system-quaternary)',
                  backgroundColor: isActive ? 'rgba(0, 113, 227, 0.05)' : 'var(--color-background-primary)',
                }}
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{
                    backgroundColor: isActive ? 'var(--color-key)' : 'var(--color-background-secondary)',
                    color: isActive ? '#ffffff' : 'var(--color-system-secondary)',
                  }}
                >
                  <Icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold" style={{ color: isActive ? 'var(--color-key)' : 'var(--color-system-primary)' }}>
                    {tab.label}
                  </div>
                  <div className="mt-1 text-xs leading-5" style={mutedText}>
                    {tab.caption}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {stage === 'idle' && (
            <>
              <IntakeSteps
                stepLabels={
                  activeTab === 'manual'
                    ? ['① 填写字段', '② 客户端校验', '③ 审核台预览发布']
                    : activeTab === 'file'
                    ? ['① 选择文件', '② 识别文档类型', '③ 确认抽取方向', '④ 对照编辑', '⑤ 推送审核']
                    : ['① 粘贴文字', '② 识别文档类型', '③ 确认抽取方向', '④ 对照编辑', '⑤ 推送审核']
                }
              />
              <div className="mt-4">
                {activeTab === 'file' && (
                  <>
                    <label
                      className="block cursor-pointer rounded-xl border border-dashed px-6 py-10 text-center transition"
                      style={{ borderColor: 'var(--color-system-tertiary)', backgroundColor: 'var(--color-background-secondary)' }}
                    >
                      <input
                        type="file"
                        accept=".pdf,.xlsx,.xls,.docx,.pptx,.csv,.txt,.md,.markdown,.json,.png,.jpg,.jpeg,.webp"
                        multiple
                        className="hidden"
                        onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
                      />
                      <div className="flex justify-center">
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-full"
                          style={{ backgroundColor: 'var(--color-background-primary)', color: 'var(--color-key)' }}
                        >
                          <FileUp size={18} />
                        </div>
                      </div>
                      <div className="mt-3 text-sm font-medium">点击选择情报资料</div>
                      <div className="mt-1 text-sm" style={mutedText}>{selectedLabel}</div>
                      <div className="mt-1 text-[11px]" style={mutedText}>
                        支持 PDF / Excel / Word / PPT / CSV / Markdown / 图片
                      </div>
                    </label>
                    <div className="mt-4 flex items-center justify-end">
                      <button
                        onClick={() => void handleUpload()}
                        disabled={selectedFiles.length === 0}
                        className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                        style={primaryButtonStyle}
                      >
                        <SendHorizontal size={15} className="mr-1.5" />
                        立即解析
                      </button>
                    </div>
                  </>
                )}

                {activeTab === 'text' && (
                  <>
                    <input
                      value={sourceTitle}
                      onChange={(event) => setSourceTitle(event.target.value)}
                      placeholder="来源标题，例如：2026 Q2 中东竞品报价"
                      className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition focus:border-[var(--color-key)]"
                      style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
                    />
                    <textarea
                      value={inputText}
                      onChange={(event) => setInputText(event.target.value)}
                      rows={10}
                      placeholder="粘贴销售线索、报价片段、会议纪要或竞品规格描述。解析器会即时抽取 competitor / pitch / price / quarter 等字段。"
                      className="mt-3 w-full rounded-lg border px-3 py-3 text-sm leading-6 outline-none transition focus:border-[var(--color-key)]"
                      style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
                    />
                    <div className="mt-4 flex items-center justify-end">
                      <button
                        onClick={() => void handleSubmitText()}
                        disabled={!inputText.trim()}
                        className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                        style={primaryButtonStyle}
                      >
                        <SendHorizontal size={15} className="mr-1.5" />
                        立即解析
                      </button>
                    </div>
                  </>
                )}

                {activeTab === 'manual' && <ManualFactForm onSuccess={handleManualSuccess} />}
              </div>
            </>
          )}

          {stage === 'parsing' && <ParsingPanel tabKind={activeTab} step={currentStep} />}

          {stage === 'confirming' && intentPreview && (
            <>
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsChatOpen(true)}
                  className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition hover:bg-[var(--color-background-secondary)]"
                  style={{ borderColor: 'var(--color-key)', color: 'var(--color-key)' }}
                >
                  <MessageSquareText size={14} />
                  💬 对此文档提问 / AI 智能问答
                </button>
              </div>
              <IntentConfirmCard
                intentPreview={intentPreview}
                extracting={extracting}
                onConfirm={(intent, source) => void runExtractFlow(intent, source)}
                onCancel={handleDiscardPreview}
              />
            </>
          )}

          {stage === 'previewing' && preview && (
            <>
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsChatOpen(true)}
                  className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition hover:bg-[var(--color-background-secondary)]"
                  style={{ borderColor: 'var(--color-key)', color: 'var(--color-key)' }}
                >
                  <MessageSquareText size={14} />
                  💬 对此文档提问 / AI 智能问答
                </button>
              </div>
              <PreviewWorkbench
                preview={preview}
                onDiscard={handleDiscardPreview}
                onSend={(edited) => void handleSendToReview(edited)}
              />
            </>
          )}

          <DocChatModal
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            extractedText={intentPreview?.extractedTextExcerpt || preview?.extractedTextExcerpt || ''}
            sourceName={intentPreview?.sourceName || preview?.sourceName || '文档'}
          />


          {stageError && (
            <div
              className="mt-4 flex items-start gap-2 rounded-lg border p-3 text-xs leading-5"
              style={{ borderColor: 'rgba(220,38,38,0.3)', backgroundColor: 'rgba(220,38,38,0.06)', color: '#b91c1c' }}
            >
              <XCircle size={14} className="mt-0.5 shrink-0" />
              <span>{stageError}</span>
            </div>
          )}
        </div>

        {currentTab && stage === 'idle' && (
          <div
            className="mt-4 flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs"
            style={{ borderColor: 'var(--color-system-quaternary)', ...mutedText }}
          >
            <currentTab.icon size={12} />
            <span>
              当前模式：{currentTab.label} · {mode === 'local' ? '本地缓存（AI 解析仍走后端）' : '在线后端 API'}
            </span>
          </div>
        )}
      </section>

      {recentActivity.length > 0 && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--color-system-tertiary)' }}>
              待审核候选（最近生成）
            </p>
            <Link to="/price_intel/reviews" className="text-xs font-medium" style={{ color: 'var(--color-key)' }}>
              前往审核台 →
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {recentActivity.map((item) => (
              <Link
                key={item.id}
                to={item.href}
                className="block rounded-lg border p-3 transition hover:bg-[var(--color-background-primary)]"
                style={{ borderColor: 'var(--color-system-quaternary)' }}
              >
                <div className="text-sm font-medium">{item.title}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-[0.18em]" style={mutedText}>{item.meta}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function IntakeSteps({ stepLabels }: { stepLabels: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em]">
      {stepLabels.map((label, idx) => (
        <span key={label} className="flex items-center gap-2">
          <span
            className="rounded-full px-2.5 py-1"
            style={{ backgroundColor: 'var(--color-background-secondary)', color: 'var(--color-system-secondary)' }}
          >
            {label}
          </span>
          {idx < stepLabels.length - 1 && <span style={{ color: 'var(--color-system-tertiary)' }}>→</span>}
        </span>
      ))}
    </div>
  );
}

function ParsingPanel({ tabKind, step }: { tabKind: IntakeTab; step: number }) {
  const steps =
    tabKind === 'file'
      ? ['上传文件到后端', '本地结构化解析与表格分块', '识别文档类型与抽取方向']
      : ['规整粘贴文本', '表格/段落分块', '识别文档类型与抽取方向'];

  return (
    <div
      className="rounded-xl border p-5"
      style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-secondary)' }}
    >
      <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--color-system-primary)' }}>
        <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-key)' }} />
        AI 正在解析…请勿关闭页面
      </div>
      <div className="mt-3 space-y-2">
        {steps.map((label, idx) => {
          const done = idx < step;
          const active = idx === step;
          return (
            <div key={label} className="flex items-center gap-2 text-xs">
              <span
                className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold"
                style={{
                  backgroundColor: done ? 'var(--color-key)' : active ? 'rgba(0,113,227,0.15)' : 'var(--color-background-primary)',
                  color: done ? '#ffffff' : active ? 'var(--color-key)' : 'var(--color-system-tertiary)',
                  border: '1px solid var(--color-system-quaternary)',
                }}
              >
                {done ? <CheckCircle2 size={12} /> : idx + 1}
              </span>
              <span style={{ color: active ? 'var(--color-system-primary)' : 'var(--color-system-secondary)' }}>
                {label}
                {active && '…'}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-[11px]" style={{ color: 'var(--color-system-tertiary)' }}>
        说明：后端同步调用 LLM（openai 兼容协议），不走队列。需保持 `127.0.0.1:8010` 后端正常运行。
      </div>
    </div>
  );
}

// ----- 意图确认卡片（confirming 阶段） -----
const INTENT_LABELS: Record<DocIntent, string> = {
  price_quote: '竞品报价表',
  spec_sheet: '产品规格书',
  tender_checklist: '招标 Checklist',
  research_note: '调研纪要',
  general: '通用文档',
};

const FIELD_LABELS: Record<string, string> = {
  competitor: '竞品厂商',
  series: '系列',
  model: '型号',
  pitch_mm: '点间距',
  brightness: '亮度',
  application: '应用场景',
  indoor_outdoor: '室内/室外',
  region: '地区',
  country: '国家',
  price_per_piece: '每片价',
  price_per_sqm: '每平米价',
  currency: '币种',
  year_quarter: '季度',
  notes: '备注/摘要',
};

function IntentConfirmCard({
  intentPreview,
  extracting,
  onConfirm,
  onCancel,
}: {
  intentPreview: ParseIntentResult;
  extracting: boolean;
  onConfirm: (intent: DocIntent, source: 'rule' | 'llm' | 'user_override') => void;
  onCancel: () => void;
}) {
  const verdict = intentPreview.intent;
  const [selected, setSelected] = useState<DocIntent>(verdict.intent);
  // needs_user_confirm 时下拉默认展开、确认按钮置灰直到用户主动选定
  const [pickerOpen, setPickerOpen] = useState<boolean>(verdict.needs_user_confirm);
  const [userPicked, setUserPicked] = useState<boolean>(!verdict.needs_user_confirm);

  const fingerprint = intentPreview.fingerprint;
  const confirmDisabled = extracting || (verdict.needs_user_confirm && !userPicked);

  const intentOptions = (Object.keys(INTENT_LABELS) as DocIntent[]);

  const fingerprintChips: string[] = [];
  if (fingerprint.competitor_mentions.length) fingerprintChips.push(`涉及竞品：${fingerprint.competitor_mentions.join(' / ')}`);
  if (fingerprint.region_mentions.length) fingerprintChips.push(`地区：${fingerprint.region_mentions.slice(0, 3).join(' / ')}`);
  fingerprintChips.push(`表格 ${fingerprint.table_count} 个`);
  fingerprintChips.push(`价格信号 ${fingerprint.price_signal_count} 处`);
  if (fingerprint.spec_signal_count) fingerprintChips.push(`规格信号 ${fingerprint.spec_signal_count} 处`);
  fingerprintChips.push(`${fingerprint.char_count} 字符`);

  return (
    <div
      className="rounded-xl border p-5"
      style={{ borderColor: 'var(--color-key)', backgroundColor: 'var(--color-background-primary)' }}
    >
      <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--color-system-primary)' }}>
        <Sparkles size={14} style={{ color: 'var(--color-key)' }} />
        已识别文档类型，请确认抽取方向
      </div>

      {/* 意图主标签 + 置信度 */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className="rounded-full px-3 py-1 text-sm font-semibold"
          style={{ backgroundColor: 'rgba(0,113,227,0.1)', color: 'var(--color-key)' }}
        >
          {INTENT_LABELS[selected] || selected}
        </span>
        <span className="text-xs" style={mutedText}>
          置信度 {(verdict.confidence * 100).toFixed(0)}% · {verdict.intent_source === 'rule' ? '规则判定' : verdict.intent_source === 'llm' ? 'AI 判定' : '人工指定'}
        </span>
      </div>
      <p className="mt-2 text-xs leading-5" style={mutedText}>
        {verdict.reason}
      </p>

      {/* 文档指纹摘要 */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {fingerprintChips.map((chip) => (
          <span
            key={chip}
            className="rounded-full px-2.5 py-1 text-[11px]"
            style={{ backgroundColor: 'var(--color-background-secondary)', color: 'var(--color-system-secondary)' }}
          >
            {chip}
          </span>
        ))}
      </div>

      {/* 抽取侧重字段 */}
      {verdict.suggested_fields.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--color-system-tertiary)' }}>
            将重点抽取
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {verdict.suggested_fields.map((field) => (
              <span
                key={field}
                className="rounded-md border px-2 py-0.5 text-[11px]"
                style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-primary)' }}
              >
                {FIELD_LABELS[field] || field}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 改一下下拉 */}
      {pickerOpen && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {intentOptions.map((option) => {
            const isActive = option === selected;
            return (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setSelected(option);
                  setUserPicked(true);
                }}
                className="rounded-lg border px-3 py-2 text-left text-xs transition"
                style={{
                  borderColor: isActive ? 'var(--color-key)' : 'var(--color-system-quaternary)',
                  backgroundColor: isActive ? 'rgba(0,113,227,0.06)' : 'var(--color-background-primary)',
                  color: isActive ? 'var(--color-key)' : 'var(--color-system-primary)',
                }}
              >
                {INTENT_LABELS[option]}
              </button>
            );
          })}
        </div>
      )}

      {/* 操作条 */}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={extracting}
          className="inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium transition hover:opacity-90 disabled:opacity-50"
          style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}
        >
          <Trash2 size={12} className="mr-1" /> 取消
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen((prev) => !prev)}
          disabled={extracting}
          className="inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium transition hover:opacity-90 disabled:opacity-50"
          style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}
        >
          <FilePenLine size={12} className="mr-1" /> 改一下 ▾
        </button>
        <button
          type="button"
          onClick={() => onConfirm(selected, userPicked && selected !== verdict.intent ? 'user_override' : verdict.intent_source)}
          disabled={confirmDisabled}
          className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          style={primaryButtonStyle}
        >
          {extracting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />}
          {extracting ? '正在抽取…' : '确认并抽取'}
        </button>
      </div>
    </div>
  );
}

// ----- 可编辑候选表单（右侧工作台） -----
const EMPTY_CAND: ParsePreviewCandidate = {
  competitor: '',
  series: null,
  model: null,
  pitch_mm: null,
  brightness: null,
  application: null,
  indoor_outdoor: null,
  region: null,
  country: null,
  price_per_piece: null,
  price_per_sqm: null,
  currency: null,
  year_quarter: null,
  notes: null,
  confidence: 0.6,
  source_excerpt: null,
  source_page: null,
  competitor_id: null,
  product_model_id: null,
  normalized_payload: {},
};

function PreviewWorkbench({
  preview,
  onDiscard,
  onSend,
}: {
  preview: ParsePreviewResult;
  onDiscard: () => void;
  onSend: (edited: ParsePreviewCandidate) => void;
}) {
  const firstCandidate = preview.candidates[0];
  const [form, setForm] = useState<ParsePreviewCandidate>({ ...EMPTY_CAND, ...(firstCandidate || {}) });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ ...EMPTY_CAND, ...(preview.candidates[0] || {}) });
  }, [preview]);

  const update = <K extends keyof ParsePreviewCandidate>(key: K, value: ParsePreviewCandidate[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = () => {
    if (!form.competitor || !String(form.competitor).trim()) {
      showToast('请填写竞品厂商名称');
      return;
    }
    setSaving(true);
    try {
      onSend(form);
    } finally {
      setSaving(false);
    }
  };

  const hasCandidates = preview.candidates.length > 0;

  return (
    <div className="space-y-3">
      {/* 顶部状态条 */}
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3"
        style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
      >
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-system-primary)' }}>
          <CheckCircle2 size={14} style={{ color: 'var(--color-success, #16a34a)' }} />
          <span className="font-semibold">AI 解析完成</span>
          <span style={{ color: 'var(--color-system-tertiary)' }}>·</span>
          <span className="text-xs" style={{ color: 'var(--color-system-secondary)' }}>
            {preview.model ? `模型：${preview.model}` : '模型未配置'} · 识别 {preview.candidates.length} 条 · {preview.extractedTextLength} 字符
          </span>
        </div>
        {preview.candidates.length > 1 && (
          <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#b45309' }}>
            注意：AI 抽到 {preview.candidates.length} 条，当前只编辑第 1 条，其余丢弃。
          </span>
        )}
      </div>

      {/* 左右两列工作台 */}
      <div className="grid gap-3 lg:grid-cols-2">
        {/* 左：AI 过程 + 原文 */}
        <div
          className="flex flex-col overflow-hidden rounded-xl border"
          style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-secondary)' }}
        >
          <div className="border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em]" style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-tertiary)' }}>
            <Bot size={12} className="mr-1 inline" /> AI 解析过程
          </div>
          <div className="space-y-1.5 border-b px-4 py-3 text-xs" style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}>
            {preview.log.map((line, idx) => (
              <div key={idx} className="flex gap-1.5 leading-5">
                <span style={{ color: 'var(--color-system-tertiary)' }}>›</span>
                <span>{line}</span>
              </div>
            ))}
            <div className="mt-2 flex flex-wrap gap-1">
              {preview.pipeline.map((p) => (
                <span key={p} className="rounded-full px-2 py-0.5 text-[10px]" style={{ backgroundColor: 'var(--color-background-primary)', color: 'var(--color-key)' }}>
                  {p}
                </span>
              ))}
            </div>
          </div>
          <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--color-system-tertiary)' }}>
            <FileText size={12} className="mr-1 inline" /> 原文摘要
          </div>
          <pre
            className="mx-4 mb-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border px-3 py-2 font-mono text-[11px] leading-5"
            style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)', color: 'var(--color-system-secondary)' }}
          >
            {preview.extractedTextExcerpt || '（未抽取到文本）'}
          </pre>
        </div>

        {/* 右：可编辑表单 */}
        <div
          className="flex flex-col overflow-hidden rounded-xl border"
          style={{ borderColor: 'var(--color-key)', backgroundColor: 'var(--color-background-primary)' }}
        >
          <div className="border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em]" style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-key)' }}>
            <Sparkles size={12} className="mr-1 inline" /> AI 填好的字段 · 请核对并修改
          </div>
          <div className="grid gap-x-3 gap-y-2.5 px-4 py-3 sm:grid-cols-2">
            <Field label="竞品 *" placeholder="Absen / Unilumin …" value={form.competitor ?? ''} onChange={(v) => update('competitor', v || null)} />
            <Field label="系列" placeholder="A25 / Umini …" value={form.series ?? ''} onChange={(v) => update('series', v || null)} />
            <Field label="型号" placeholder="A25 Outdoor …" value={form.model ?? ''} onChange={(v) => update('model', v || null)} />
            <NumberField label="点间距 (mm)" value={form.pitch_mm} onChange={(v) => update('pitch_mm', v)} />
            <NumberField label="亮度 (nit)" value={form.brightness} onChange={(v) => update('brightness', v != null ? Math.round(v) : null)} />
            <SelectField label="室内/室外" value={form.indoor_outdoor ?? ''} options={['', 'Indoor', 'Outdoor']} onChange={(v) => update('indoor_outdoor', v || null)} />
            <Field label="应用场景" placeholder="Rental / Fixed …" value={form.application ?? ''} onChange={(v) => update('application', v || null)} />
            <SelectField label="币种" value={form.currency ?? ''} options={['', 'USD', 'EUR', 'CNY', 'GBP', 'JPY']} onChange={(v) => update('currency', v || null)} />
            <NumberField label="每片价" value={form.price_per_piece} onChange={(v) => update('price_per_piece', v)} />
            <NumberField label="每平米价" value={form.price_per_sqm} onChange={(v) => update('price_per_sqm', v)} />
            <Field label="地区" placeholder="Middle East …" value={form.region ?? ''} onChange={(v) => update('region', v || null)} />
            <Field label="国家" placeholder="UAE …" value={form.country ?? ''} onChange={(v) => update('country', v || null)} />
            <Field label="季度" placeholder="2026Q1" value={form.year_quarter ?? ''} onChange={(v) => update('year_quarter', v || null)} />
            <NumberField label="置信度 (0-1)" value={form.confidence} onChange={(v) => update('confidence', v)} step="0.05" />
            <div className="sm:col-span-2">
              <Label text="备注" />
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => update('notes', e.target.value || null)}
                rows={2}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-xs outline-none transition focus:border-[var(--color-key)]"
                style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
              />
            </div>
            <div className="sm:col-span-2">
              <Label text="原文证据片段" />
              <textarea
                value={form.source_excerpt ?? ''}
                onChange={(e) => update('source_excerpt', e.target.value || null)}
                rows={2}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-xs italic outline-none transition focus:border-[var(--color-key)]"
                style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
              />
            </div>
          </div>

          {!hasCandidates && (
            <div className="mx-4 mb-3 rounded-lg border px-3 py-2 text-xs" style={{ borderColor: 'rgba(245, 158, 11, 0.3)', backgroundColor: 'rgba(245, 158, 11, 0.08)', color: '#b45309' }}>
              AI 未识别到价格事实，已给你一张空表。可以按原文自己补全后再推送。
            </div>
          )}
        </div>
      </div>

      {/* 底部粘性操作条 */}
      <div
        className="sticky bottom-0 z-10 -mx-1 mt-2 flex flex-wrap items-center justify-end gap-2 rounded-xl border px-4 py-2.5"
        style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)', backdropFilter: 'blur(6px)' }}
      >
        <span className="mr-auto text-xs" style={{ color: 'var(--color-system-tertiary)' }}>
          核对完字段后再推送。推送后该候选会以 `pending_review` 进入审核台，由审批人点击"发布"才会进入正式事实表。
        </span>
        <button
          type="button"
          onClick={onDiscard}
          disabled={saving}
          className="inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium transition hover:opacity-90 disabled:opacity-50"
          style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}
        >
          <Trash2 size={12} className="mr-1" /> 丢弃重来
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving}
          className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: 'var(--color-key)', color: '#ffffff' }}
        >
          {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <SendHorizontal size={12} className="mr-1" />}
          推送到审核台
        </button>
      </div>
    </div>
  );
}

// ----- 表单小组件 -----
function Label({ text }: { text: string }) {
  return (
    <label className="text-[11px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--color-system-tertiary)' }}>
      {text}
    </label>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <Label text={label} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-lg border px-3 py-2 text-xs outline-none transition focus:border-[var(--color-key)]"
        style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
      />
    </div>
  );
}

function NumberField({ label, value, onChange, step = 'any' }: { label: string; value: number | null | undefined; onChange: (v: number | null) => void; step?: string }) {
  return (
    <div>
      <Label text={label} />
      <input
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') onChange(null);
          else {
            const n = Number(v);
            onChange(Number.isFinite(n) ? n : null);
          }
        }}
        className="mt-1 w-full rounded-lg border px-3 py-2 text-xs outline-none transition focus:border-[var(--color-key)]"
        style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
      />
    </div>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <div>
      <Label text={label} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-xs outline-none transition focus:border-[var(--color-key)]"
        style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt || '（未指定）'}
          </option>
        ))}
      </select>
    </div>
  );
}

export function DocChatModal({
  isOpen,
  onClose,
  extractedText,
  sourceName,
}: {
  isOpen: boolean;
  onClose: () => void;
  extractedText: string;
  sourceName: string;
}) {
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string; citations?: any[] }>>([
    {
      role: 'assistant',
      content: `您好！我已经完成了文档《${sourceName}》的结构化解析并沉淀进通用知识库。您可以直接向我提问该文档中的任何内容、价格参数或条款！`,
    },
  ]);
  const [inputQuestion, setInputQuestion] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSend = async (qText?: string) => {
    const query = (qText || inputQuestion).trim();
    if (!query || loading) return;

    setInputQuestion('');
    setMessages((prev) => [...prev, { role: 'user', content: query }]);
    setLoading(true);

    try {
      const historyPayload = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await askDocumentQA(extractedText, query, historyPayload);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.answer,
          citations: res.citations,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `问答异常: ${(err as Error).message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div
        className="flex h-[620px] w-[750px] max-w-full flex-col overflow-hidden rounded-2xl border shadow-2xl transition-all"
        style={{
          backgroundColor: 'var(--color-background-primary)',
          borderColor: 'var(--color-system-quaternary)',
        }}
      >
        {/* Modal Header */}
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: 'var(--color-system-quaternary)' }}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(0,113,227,0.1)] text-[var(--color-key)]">
              <MessageSquareText size={18} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-system-primary)]">Universal Doc Hub AI 智能问答</h3>
              <p className="text-xs text-[var(--color-system-secondary)]">当前提问对象：{sourceName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-[var(--color-system-secondary)] hover:bg-[var(--color-background-secondary)]"
          >
            ✕
          </button>
        </div>

        {/* Chat History */}
        <div className="flex-1 overflow-y-auto space-y-4 p-6">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-xs leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[var(--color-key)] text-white'
                    : 'border bg-[var(--color-background-secondary)] text-[var(--color-system-primary)]'
                }`}
                style={msg.role === 'assistant' ? { borderColor: 'var(--color-system-quaternary)' } : {}}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {msg.citations && msg.citations.length > 0 && (
                  <div className="mt-2.5 border-t pt-2 border-dashed border-[var(--color-system-tertiary)]/30">
                    <div className="text-[10px] font-semibold text-[var(--color-system-secondary)] uppercase tracking-wider mb-1">
                      📍 引用溯源
                    </div>
                    {msg.citations.map((c, cIdx) => (
                      <div key={cIdx} className="text-[11px] opacity-90 text-[var(--color-key)]">
                        [第 {c.page_number || 1} 页]: {c.excerpt}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-system-secondary)]">
              <Loader2 size={14} className="animate-spin text-[var(--color-key)]" />
              <span>AI 正在检索文档 Chunk 并推理回答...</span>
            </div>
          )}
        </div>

        {/* Chat Input */}
        <div className="border-t p-4" style={{ borderColor: 'var(--color-system-quaternary)' }}>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputQuestion}
              onChange={(e) => setInputQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleSend()}
              placeholder="对此文档提问（例如：价格是多少？包含什么型号和参数？）..."
              className="flex-1 rounded-xl border px-4 py-2.5 text-xs outline-none focus:border-[var(--color-key)]"
              style={{
                borderColor: 'var(--color-system-quaternary)',
                backgroundColor: 'var(--color-background-secondary)',
              }}
            />
            <button
              onClick={() => void handleSend()}
              disabled={loading || !inputQuestion.trim()}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-key)' }}
            >
              <SendHorizontal size={14} />
              发送
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

